import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Modal,
  message,
  Radio,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import defaultMarkerIcon from 'routes/app/collections/mapMarkerIcon';
import {
  Collection,
  CollectionPoint,
  CollectionPointShift,
  collectionWindow,
  declaredText,
  useCancelCollection,
  useCollectionPoints,
  useCollections,
  useCreateCollection,
} from 'services/CollectionsV2Service';
import AmountInput from 'components/amountInput';
import { formatAmount } from 'util/amount';
import { useSelector } from 'react-redux';
import { authProfile } from 'redux/auth/selectors';
import { useAddCollection } from 'services/journeys';
import { useJourneyDashboardContext } from '../context';
import SITELINKS from 'constants/sitelinks';

const { Text } = Typography;

const PAGE_SIZE = 10;
const TIME_FORMAT = 'HH:mm';
const CUSTOM = 'custom';

const withTime = (date: Dayjs, time: string | null) => {
  if (!time) return date.hour(9).minute(0).second(0).millisecond(0);
  const [hour, minute] = time.split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return date;
  return date.hour(hour).minute(minute).second(0).millisecond(0);
};

interface Props {
  open: boolean;
  journeyId: string;
  onClose: () => void;
}

type Tab = 'fixed' | 'on_demand';

const Place: React.FC<{
  storeName: string;
  marketplaceName: string;
  address: string;
  lat: number;
  lng: number;
}> = ({ storeName, marketplaceName, address, lat, lng }) => (
  <Card size="small">
    <Row gutter={16} align="middle">
      <Col xs={24} md={12}>
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 16 }}>
            {storeName}
          </Text>
          <Text type="secondary">{marketplaceName}</Text>
          <Text type="secondary">{address}</Text>
        </Space>
      </Col>
      <Col xs={24} md={12}>
        <div style={{ height: 160 }}>
          <MapContainer
            key={`${lat},${lng}`}
            center={[lat, lng]}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[lat, lng]} icon={defaultMarkerIcon} />
          </MapContainer>
        </div>
      </Col>
    </Row>
  </Card>
);

const AddCollectionModal: React.FC<Props> = ({
  open,
  journeyId,
  onClose,
}: Props) => {
  // La colecta se ingresa en el centro de la ruta, que no siempre es el del perfil del usuario.
  const profileCenterId = useSelector(authProfile)?.paquerypoint?.id;
  const { distributionCenterId: journeyCenterId, schedule } =
    useJourneyDashboardContext();
  const distributionCenterId = journeyCenterId ?? profileCenterId;
  const journeyDay = schedule?.date ? dayjs(schedule.date) : dayjs();
  const [tab, setTab] = useState<Tab>('fixed');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [chosen, setChosen] = useState<CollectionPoint | null>(null);
  const [shiftKey, setShiftKey] = useState<number | typeof CUSTOM>(CUSTOM);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [time, setTime] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(20);
  // Viene con el del punto y se puede cambiar, como el turno. Siempre viaja:
  // vaciarlo manda null, que es una colecta sin monto.
  const [amount, setAmount] = useState<number | null>(null);
  const [requestDay, setRequestDay] = useState<Dayjs>(journeyDay);
  const [requestSearch, setRequestSearch] = useState('');
  const [requestPage, setRequestPage] = useState(0);
  const [chosenRequest, setChosenRequest] = useState<Collection | null>(null);

  useEffect(() => {
    if (open) setRequestDay(journeyDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schedule?.date]);

  const points = useCollectionPoints(
    { active: true, q: search || undefined, page, pageSize: PAGE_SIZE },
    open,
  );
  const requests = useCollections(
    {
      status: ['requested'],
      type: ['on_demand'],
      dateFrom: requestDay.startOf('day').toISOString(),
      dateTo: requestDay.add(1, 'day').startOf('day').toISOString(),
      q: requestSearch || undefined,
      page: requestPage,
      pageSize: PAGE_SIZE,
    },
    open,
  );

  const { mutateAsync: createCollection, isPending: isCreating } =
    useCreateCollection();
  const { mutateAsync: addCollection, isPending: isAdding } =
    useAddCollection();
  const { mutateAsync: cancelCollection } = useCancelCollection();

  const busy = isCreating || isAdding;

  const reset = () => {
    setChosen(null);
    setChosenRequest(null);
    setShiftKey(CUSTOM);
    setTime(null);
    setDuration(20);
    setAmount(null);
  };

  const close = () => {
    reset();
    setTab('fixed');
    onClose();
  };

  const applyShift = (shift: CollectionPointShift) => {
    setShiftKey(shift.id);
    setTime(shift.startTime);
    setDuration(shift.estimatedDurationMinutes);
  };

  const pickPoint = (point: CollectionPoint) => {
    setChosen(point);
    setDate(dayjs());
    setAmount(point.amount ?? null);
    const shifts = point.shifts ?? [];
    if (shifts.length > 0) applyShift(shifts[0]);
    else {
      setShiftKey(CUSTOM);
      setTime(null);
      setDuration(20);
    }
  };

  // La colecta es del cliente: ya existe, solo se suma a la ruta. Si falla,
  // no se cancela; vuelve a la lista.
  const confirmRequest = async (request: Collection) => {
    try {
      await addCollection({
        journeyId,
        collectionId: request.id,
        storeName: request.storeName,
      });
      close();
    } catch {
      setChosenRequest(null);
    }
  };

  const confirm = async () => {
    if (chosenRequest) {
      await confirmRequest(chosenRequest);
      return;
    }
    if (!chosen) return;
    const scheduledStart = withTime(date, time).toISOString();
    let created: { id: number } | null = null;
    try {
      created = await createCollection({
        collectionPointId: chosen.id,
        journeyId,
        scheduledStart,
        estimatedDurationMinutes: duration,
        // Sin el flag no viaja: ms-collections copia el del punto.
        amount,
        distributionCenterId,
      });
      await addCollection({ journeyId, collectionId: created.id });
      close();
    } catch (error) {
      if (created) {
        cancelCollection({ id: created.id }).catch(() => {});
      }
      const failed = error as { name?: string; message?: string };
      if (failed?.name !== 'JourneyError') {
        message.error(failed?.message ?? 'No se pudo crear la colecta');
      }
      setChosen(null);
    }
  };

  const pointColumns: TableColumnsType<CollectionPoint> = [
    { title: '#', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Tienda',
      key: 'store',
      render: (_v, record) => (
        <Space direction="vertical" size={0}>
          <span>{record.storeName}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.marketplaceName}
          </Text>
        </Space>
      ),
    },
    { title: 'Dirección', dataIndex: 'address', key: 'address' },
    {
      title: 'Turnos',
      dataIndex: 'shifts',
      key: 'shifts',
      width: 220,
      render: (shifts: CollectionPointShift[]) =>
        shifts?.length ? (
          <Space size={4} wrap>
            {shifts.map((shift) => (
              <Tag key={shift.id} style={{ margin: 0 }}>
                {shift.startTime} · {shift.estimatedDurationMinutes} min
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">Sin turnos</Text>
        ),
    },
    {
      key: 'add',
      width: 110,
      render: (_v, record) => (
        <Button type="primary" size="small" onClick={() => pickPoint(record)}>
          Agregar
        </Button>
      ),
    },
  ];

  const requestColumns: TableColumnsType<Collection> = [
    { title: '#', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Tienda',
      key: 'store',
      render: (_v, record) => (
        <Space direction="vertical" size={0}>
          <span>{record.storeName}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.marketplaceName}
          </Text>
        </Space>
      ),
    },
    { title: 'Dirección', dataIndex: 'address', key: 'address' },
    {
      title: 'Franja',
      key: 'window',
      width: 120,
      render: (_v, record) =>
        collectionWindow(
          record.scheduledStart,
          record.scheduledEnd,
          undefined,
          record.utcOffset,
        ),
    },
    {
      title: 'Declarado',
      key: 'declared',
      width: 140,
      render: (_v, record) =>
        declaredText(record.declaredQuantity, record.declaredDescription) ||
        '-',
    },
    {
      title: 'Observaciones',
      dataIndex: 'notes',
      key: 'notes',
      width: 160,
      ellipsis: { showTitle: false },
      render: (notes?: string | null) =>
        notes ? (
          <Tooltip title={notes} placement="topLeft">
            {notes}
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      key: 'add',
      width: 110,
      render: (_v, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => setChosenRequest(record)}
        >
          Agregar
        </Button>
      ),
    },
  ];

  const requestCount = requests.data?.totalElements ?? 0;
  const picked = chosen || chosenRequest;

  return (
    <Modal
      open={open}
      onCancel={close}
      width={900}
      destroyOnClose
      title={`Agregar colecta a la ruta ${journeyId}`}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {tab === 'fixed' ? (
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() =>
                window.open(SITELINKS.collections.points, '_blank')
              }
            >
              Dar de alta un punto de colecta
            </Button>
          ) : (
            <span />
          )}
          <Space>
            {picked && (
              <Button onClick={reset} disabled={busy}>
                {chosenRequest ? 'Elegir otra colecta' : 'Elegir otra tienda'}
              </Button>
            )}
            <Button onClick={close} disabled={busy}>
              Cancelar
            </Button>
            <Button
              type="primary"
              disabled={!picked}
              loading={busy}
              onClick={confirm}
            >
              Agregar a la ruta
            </Button>
          </Space>
        </div>
      }
    >
      {!picked && (
        <Tabs
          activeKey={tab}
          onChange={(key) => setTab(key as Tab)}
          items={[
            {
              key: 'fixed',
              label: 'Colecta fija',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Elegí la tienda</Text>
                  <Input.Search
                    placeholder="Buscar por tienda, dirección o marketplace"
                    allowClear
                    onSearch={(value) => {
                      setPage(0);
                      setSearch(value);
                    }}
                  />
                  <Table
                    rowKey="id"
                    size="small"
                    columns={pointColumns}
                    dataSource={points.data?.items ?? []}
                    loading={points.isFetching}
                    pagination={{
                      current: page + 1,
                      pageSize: PAGE_SIZE,
                      total: points.data?.totalElements ?? 0,
                      showSizeChanger: false,
                      onChange: (next) => setPage(next - 1),
                    }}
                  />
                </Space>
              ),
            },
            {
              key: 'on_demand',
              label: (
                <Space size={4}>
                  A demanda
                  {requestCount > 0 && (
                    <Tag color="magenta">{requestCount}</Tag>
                  )}
                </Space>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text type="secondary">
                    Las que pidieron los clientes y todavía no tienen ruta.
                  </Text>
                  <Space wrap>
                    <DatePicker
                      value={requestDay}
                      format="DD/MM/YYYY"
                      allowClear={false}
                      onChange={(value) => {
                        if (!value) return;
                        setRequestPage(0);
                        setRequestDay(value);
                      }}
                    />
                    <Input.Search
                      placeholder="Buscar por tienda"
                      allowClear
                      style={{ width: 320 }}
                      onSearch={(value) => {
                        setRequestPage(0);
                        setRequestSearch(value);
                      }}
                    />
                  </Space>
                  <Table
                    rowKey="id"
                    size="small"
                    columns={requestColumns}
                    dataSource={requests.data?.items ?? []}
                    loading={requests.isFetching}
                    locale={{
                      emptyText: (
                        <Empty description="No hay colectas a demanda para ese día" />
                      ),
                    }}
                    pagination={{
                      current: requestPage + 1,
                      pageSize: PAGE_SIZE,
                      total: requestCount,
                      showSizeChanger: false,
                      onChange: (next) => setRequestPage(next - 1),
                    }}
                  />
                </Space>
              ),
            },
          ]}
        />
      )}

      {chosenRequest && (
        <Spin spinning={busy}>
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Place
              storeName={chosenRequest.storeName}
              marketplaceName={chosenRequest.marketplaceName}
              address={chosenRequest.address}
              lat={chosenRequest.lat}
              lng={chosenRequest.lng}
            />
            <Space size={32} align="start" wrap>
              <Space direction="vertical" size={4}>
                <Text>Franja del cliente</Text>
                <Text style={{ fontSize: 16 }}>
                  {collectionWindow(
                    chosenRequest.scheduledStart,
                    chosenRequest.scheduledEnd,
                    'DD/MM/YYYY',
                    chosenRequest.utcOffset,
                  )}
                </Text>
              </Space>
              <Space direction="vertical" size={4}>
                <Text>Declarado</Text>
                <Text style={{ fontSize: 16 }}>
                  {declaredText(
                    chosenRequest.declaredQuantity,
                    chosenRequest.declaredDescription,
                  ) || '-'}
                </Text>
              </Space>
              <Space direction="vertical" size={4}>
                <Text>Valor de la colecta</Text>
                <Text style={{ fontSize: 16 }}>
                  {formatAmount(chosenRequest.amount)}
                </Text>
              </Space>
              {chosenRequest.notes && (
                <Space direction="vertical" size={4} style={{ maxWidth: 420 }}>
                  <Text>Observaciones</Text>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>
                    {chosenRequest.notes}
                  </Text>
                </Space>
              )}
            </Space>
            <Alert
              type="info"
              showIcon
              message="La franja la pidió el cliente y no se cambia: el driver tiene que llegar entre esas horas. El tiempo en la tienda es la espera de la ruta."
            />
          </Space>
        </Spin>
      )}

      {chosen && (
        <Spin spinning={busy}>
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Place
              storeName={chosen.storeName}
              marketplaceName={chosen.marketplaceName}
              address={chosen.address}
              lat={chosen.lat}
              lng={chosen.lng}
            />

            <div>
              <Text strong>¿Cuándo pasa el driver?</Text>
              <Text type="secondary" style={{ display: 'block', marginTop: 2 }}>
                Cada turno indica la hora de llegada y el tiempo de permanencia
                del driver en tienda.
              </Text>
              <div style={{ marginTop: 8 }}>
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  value={shiftKey}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (value === CUSTOM) {
                      setShiftKey(CUSTOM);
                      return;
                    }
                    const shift = (chosen.shifts ?? []).find(
                      (s) => s.id === value,
                    );
                    if (shift) applyShift(shift);
                  }}
                >
                  {(chosen.shifts ?? []).map((shift) => (
                    <Radio.Button key={shift.id} value={shift.id}>
                      {shift.startTime} · {shift.estimatedDurationMinutes} min
                    </Radio.Button>
                  ))}
                  <Radio.Button value={CUSTOM}>Otro horario</Radio.Button>
                </Radio.Group>
              </div>
              {(chosen.shifts ?? []).length === 0 && (
                <Text
                  type="secondary"
                  style={{ display: 'block', marginTop: 8 }}
                >
                  Este punto no tiene turnos cargados: cargá acá la hora de
                  llegada y el tiempo en la tienda.
                </Text>
              )}
            </div>

            <Space size={16} align="start" wrap>
              <Space direction="vertical" size={4}>
                <Text>Fecha</Text>
                <DatePicker
                  value={date}
                  format="DD/MM/YYYY"
                  allowClear={false}
                  disabledDate={(day) => day.isBefore(dayjs(), 'day')}
                  onChange={(value) => value && setDate(value)}
                />
              </Space>
              {shiftKey === CUSTOM ? (
                <>
                  <Space direction="vertical" size={4}>
                    <Text>Hora de llegada a la tienda</Text>
                    <TimePicker
                      popupClassName="hora-turno"
                      value={withTime(date, time)}
                      format={TIME_FORMAT}
                      minuteStep={5}
                      allowClear={false}
                      onChange={(value) =>
                        value && setTime(value.format(TIME_FORMAT))
                      }
                    />
                  </Space>
                  <Space direction="vertical" size={4}>
                    <Text>Tiempo en la tienda</Text>
                    <InputNumber
                      min={1}
                      max={240}
                      addonAfter="min"
                      value={duration}
                      onChange={(value) => setDuration(value ?? 1)}
                    />
                  </Space>
                </>
              ) : (
                <Space direction="vertical" size={4}>
                  <Text>Turno elegido</Text>
                  <Text style={{ fontSize: 16, lineHeight: '32px' }}>
                    Llega {time} · se queda {duration} min (hasta las{' '}
                    {withTime(date, time)
                      .add(duration, 'minute')
                      .format(TIME_FORMAT)}
                    )
                  </Text>
                </Space>
              )}
              <Space direction="vertical" size={4}>
                <Text>Valor de la colecta</Text>
                <AmountInput value={amount} onChange={setAmount} />
              </Space>
            </Space>
          </Space>
        </Spin>
      )}
    </Modal>
  );
};

export default AddCollectionModal;
